import { useState } from 'react';
import { TeamMember } from '@/mocks/wizard';

interface StepTeamProps {
  team: TeamMember[];
  onChange: (team: TeamMember[]) => void;
}

const ROLES = ['CEO', 'CTO', 'Фінансовий директор', 'Аналітик', 'Диспетчер', 'Технік', 'Менеджер', 'Спостерігач'];

const ROLE_COLORS: Record<string, string> = {
  CEO: 'var(--accent)',
  CTO: '#1F5B8F',
  'Фінансовий директор': '#2E7D32',
  Аналітик: '#B8860B',
  Диспетчер: '#6B4FC8',
  Технік: '#C87A1A',
  Менеджер: '#0D7A3C',
  Спостерігач: '#6B6B74',
};

const inputCls = 'w-full px-3.5 py-2.5 rounded-lg text-sm border transition-all outline-none focus:border-gray-400';
const inputStyle = { background: '#FAFAFA', borderColor: '#E5E5E8', color: '#1A1A1A', fontFamily: 'Inter, sans-serif' };

function getInitials(name: string) {
  return name.split(' ').map((p) => p[0] ?? '').join('').toUpperCase().slice(0, 2) || '?';
}

function MemberRow({ member, onRemove, onUpdate }: {
  member: TeamMember;
  onRemove: () => void;
  onUpdate: (updated: TeamMember) => void;
}) {
  const roleColor = ROLE_COLORS[member.role] ?? '#6B6B74';

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl"
      style={{ background: '#fff', border: '1px solid #E5E5E8' }}
    >
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
        style={{ background: `${roleColor}20`, color: roleColor }}
      >
        {getInitials(member.name)}
      </div>
      <div className="flex-1 grid grid-cols-3 gap-2 min-w-0">
        <input
          type="text"
          value={member.name}
          onChange={(e) => onUpdate({ ...member, name: e.target.value })}
          placeholder="Ім'я та прізвище"
          className={inputCls}
          style={inputStyle}
        />
        <input
          type="email"
          value={member.email}
          onChange={(e) => onUpdate({ ...member, email: e.target.value })}
          placeholder="email@company.com"
          className={inputCls}
          style={inputStyle}
        />
        <select
          value={member.role}
          onChange={(e) => onUpdate({ ...member, role: e.target.value })}
          className={inputCls}
          style={inputStyle}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer flex-shrink-0 transition-all"
        style={{ background: 'color-mix(in srgb, var(--err) 10%, transparent)', color: 'var(--err)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--err) 18%, transparent)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--err) 10%, transparent)'; }}
      >
        <i className="ri-delete-bin-line text-sm" />
      </button>
    </div>
  );
}

export default function StepTeam({ team, onChange }: StepTeamProps) {
  const [bulkEmail, setBulkEmail] = useState('');
  const [bulkRole, setBulkRole] = useState('Аналітик');

  const addMember = () => {
    const newMember: TeamMember = {
      id: `member-${Date.now()}`,
      name: '',
      email: '',
      role: 'Аналітик',
    };
    onChange([...team, newMember]);
  };

  const removeMember = (id: string) => onChange(team.filter((m) => m.id !== id));

  const updateMember = (updated: TeamMember) =>
    onChange(team.map((m) => (m.id === updated.id ? updated : m)));

  const handleBulkInvite = () => {
    if (!bulkEmail.trim()) return;
    const emails = bulkEmail.split(/[,\n]/).map((e) => e.trim()).filter(Boolean);
    const newMembers: TeamMember[] = emails.map((email) => ({
      id: `member-${Date.now()}-${Math.random()}`,
      name: '',
      email,
      role: bulkRole,
    }));
    onChange([...team, ...newMembers]);
    setBulkEmail('');
  };

  return (
    <div className="max-w-3xl mx-auto w-full">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#1A1A1A', letterSpacing: '-0.02em' }}>
          Запросити команду
        </h2>
        <p className="text-sm" style={{ color: '#8B8B94' }}>
          Додайте колег одразу або пізніше у розділі Команда
        </p>
      </div>

      {/* Bulk invite */}
      <div
        className="p-4 rounded-xl mb-5"
        style={{ background: '#F7F7F9', border: '1px solid #E5E5E8' }}
      >
        <p className="text-xs font-semibold mb-3" style={{ color: '#8B8B94', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Масове запрошення
        </p>
        <div className="flex gap-3">
          <textarea
            value={bulkEmail}
            onChange={(e) => setBulkEmail(e.target.value)}
            placeholder="Введіть email через кому або з нового рядка&#10;a.petrenko@company.ua, b.koval@company.ua"
            rows={3}
            className="flex-1 px-3.5 py-2.5 rounded-lg text-sm border transition-all outline-none resize-none"
            style={{ ...inputStyle, fontFamily: 'Inter, sans-serif' }}
          />
          <div className="flex flex-col gap-2 flex-shrink-0">
            <select
              value={bulkRole}
              onChange={(e) => setBulkRole(e.target.value)}
              className="px-3.5 py-2.5 rounded-lg text-sm border outline-none"
              style={{ ...inputStyle, minWidth: '150px' }}
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button
              type="button"
              onClick={handleBulkInvite}
              className="px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
              style={{ background: '#1A1A1A', color: '#fff' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#2A2A2A'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#1A1A1A'; }}
            >
              <i className="ri-user-add-line mr-1.5" />
              Додати
            </button>
          </div>
        </div>
      </div>

      {/* Member list */}
      {team.length > 0 ? (
        <div className="space-y-2 mb-5">
          <div className="grid grid-cols-3 gap-2 px-12 pb-1">
            {['Ім\'я та прізвище', 'Email', 'Роль'].map((h) => (
              <p key={h} className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#AFAFB8' }}>{h}</p>
            ))}
          </div>
          {team.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              onRemove={() => removeMember(member.id)}
              onUpdate={updateMember}
            />
          ))}
        </div>
      ) : (
        <div
          className="text-center py-10 rounded-xl mb-5"
          style={{ background: '#F7F7F9', border: '2px dashed #E5E5E8' }}
        >
          <i className="ri-team-line text-3xl mb-2 block" style={{ color: '#DCDCE0' }} />
          <p className="text-sm font-medium mb-1" style={{ color: '#AFAFB8' }}>Команда порожня</p>
          <p className="text-xs" style={{ color: '#CCCCCC' }}>
            Додайте учасників вручну або через масове запрошення вище
          </p>
        </div>
      )}

      {/* Add one button */}
      <button
        type="button"
        onClick={addMember}
        className="w-full py-3 rounded-xl text-sm font-medium cursor-pointer transition-all flex items-center justify-center gap-2"
        style={{ background: '#fff', border: '2px dashed #E5E5E8', color: '#6B6B74' }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#CCCCCC'; e.currentTarget.style.color = '#1A1A1A'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E5E8'; e.currentTarget.style.color = '#6B6B74'; }}
      >
        <i className="ri-add-line" />
        Додати учасника вручну
      </button>

      {/* Summary */}
      {team.length > 0 && (
        <div
          className="mt-4 flex items-center gap-3 p-4 rounded-xl"
          style={{ background: '#E8F5EE', border: '1px solid rgba(46,125,50,0.2)' }}
        >
          <i className="ri-team-line text-lg flex-shrink-0" style={{ color: '#2E7D32' }} />
          <p className="text-sm" style={{ color: '#2E7D32' }}>
            <strong>{team.length} учасників</strong> будуть запрошені після завершення налаштування.
            Кожен отримає email із посиланням для активації.
          </p>
        </div>
      )}
    </div>
  );
}
